import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSite } from '../content/SiteContext';

// Estilo de fondo consistente con la primera sección
const gradientStyle = {
  background: 'linear-gradient(-45deg, #1e1b4b, #4c1d95, #be185d, #1e293b)',
  backgroundSize: '400% 400%',
  animation: 'gradientBG 15s ease infinite',
  width: '100%',
  height: '100%',
  position: 'absolute',
  inset: 0,
  zIndex: 0,
};

// Agregamos la animación global al documento si no existe
if (typeof document !== 'undefined' && !document.getElementById('gradientBGKeyframes')) {
  const style = document.createElement('style');
  style.id = 'gradientBGKeyframes';
  style.innerHTML = `
    @keyframes gradientBG {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `;
  document.head.appendChild(style);
}


const ProjectsSection = () => {
  const { content, t } = useSite();
  const section = content.projects;
  const projectVideos = section.items || [];
  const featuredProjects = projectVideos.filter((project) => project.featured);
  // Solo se muestran las categorías que tienen al menos un proyecto
  const categories = [
    ...(featuredProjects.length ? [{ id: 'featured', label: section.filterFeatured }] : []),
    { id: 'all', label: section.filterAll },
    ...(section.categories || []).filter((cat) => projectVideos.some((p) => p.category === cat.id)),
  ];
  const [selectedCategory, setSelectedCategory] = useState(featuredProjects.length ? 'featured' : 'all');
  // Si el admin quita la categoría elegida, volver a la primera disponible
  const activeCategory = categories.some((c) => c.id === selectedCategory) ? selectedCategory : categories[0].id;
  const [hoveredProject, setHoveredProject] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  const categoryLabel = (id) => {
    const cat = (section.categories || []).find((c) => c.id === id);
    return cat ? t(cat.label) : '';
  };

  const filteredProjects = activeCategory === 'all'
    ? projectVideos
    : activeCategory === 'featured'
    ? featuredProjects
    : projectVideos.filter(project => project.category === activeCategory);

  const openVideoInNewTab = (videoId) => {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  };

  const toggleVideoPlay = (videoId) => {
    setPlayingVideo(playingVideo === videoId ? null : videoId);
  };

  const openWhatsApp = () => {
    const phoneNumber = String(section.whatsappNumber || '').replace(/\D/g, '');
    const message = t(section.whatsappMessage);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="proyectos" className="py-20 md:py-28 relative overflow-hidden">
      {/* Fondo animado consistente con la primera sección */}
      <div style={gradientStyle}></div>
      {/* Capa de oscurecimiento para mejor contraste del texto */}
      <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-20 z-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-20">
        {/* Header Section */}
        <div className="text-center mb-16 md:mb-20">
          
          <motion.h2
            className="section-heading text-white reveal mb-6"
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: 800,
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              lineHeight: '1.1'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {t(section.title1)}{' '}
            <span
              className="text-gradient relative"
              style={{
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                backgroundImage: 'linear-gradient(135deg, #a78bfa 0%, #f472b6 50%, #06b6d4 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradient 3s ease infinite'
              }}
            >
              {t(section.title2)}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </span>
          </motion.h2>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          >
            {activeCategory === 'featured' ? t(section.subtitleFeatured) : t(section.subtitleAll)}
          </motion.p>
        </div>

        {/* Category Filter */}
        <motion.div 
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25'
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-gray-700'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t(category.label)}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeCategory}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {filteredProjects.map((video, index) => (
              <motion.div
                key={`${video.youtubeId}-${index}`}
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onHoverStart={() => setHoveredProject(index)}
                onHoverEnd={() => setHoveredProject(null)}
              >
                {/* Card Container */}
                <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 shadow-2xl group-hover:shadow-purple-500/20 transition-all duration-500 group-hover:scale-[1.02] group-hover:border-purple-500/30">
                  
                  {/* Animated Border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-pink-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                  
                  {/* Video Container */}
                  <div className="relative overflow-hidden rounded-t-2xl cursor-pointer" onClick={() => toggleVideoPlay(video.youtubeId)}>
                    <div className="relative w-full aspect-video">
                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {playingVideo === video.youtubeId ? (
                        <iframe
                          className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700"
                          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                          title={t(video.title)}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <iframe
                          className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0&modestbranding=1`}
                          title={t(video.title)}
                          frameBorder="0"
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      )}
                      
                      {/* Play indicator */}
                      <motion.div 
                        className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full p-2 z-20"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                      >
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </motion.div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 md:p-8 relative">
                    {/* Category Badge */}
                    <motion.span 
                      className="inline-block px-3 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full text-xs font-medium text-purple-300 mb-4"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                    >
                      {categoryLabel(video.category)}
                    </motion.span>
                    
                    {/* Title */}
                    <motion.h3 
                      className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-pink-400 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                    >
                      {t(video.title)}
                    </motion.h3>
                    
                    {/* Description */}
                    <motion.p 
                      className="text-gray-400 leading-relaxed mb-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                    >
                      {t(video.description)}
                    </motion.p>

                    {/* Action Button */}
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        openVideoInNewTab(video.youtubeId);
                      }}
                      className="inline-flex items-center gap-2 text-purple-400 font-medium hover:text-purple-300 transition-colors duration-300 group/btn"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.9 + index * 0.1 }}
                    >
                      {t(section.viewProject)}
                      <motion.svg 
                        className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </motion.svg>
                    </motion.button>

                    {/* Floating elements */}
                    <AnimatePresence>
                      {hoveredProject === index && (
                        <motion.div
                          className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-xl"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <p className="text-gray-400 mb-6">{t(section.ctaQuestion)}</p>
          <motion.button
            onClick={openWhatsApp}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-green-500 rounded-full font-semibold text-white shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300 group"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {t(section.ctaButton)}
            <motion.svg 
              className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.566z"/>
            </motion.svg>
          </motion.button>
        </motion.div>
      </div>


    </section>
  );
};

export default ProjectsSection; 